/**
 * An idea of bringing the simplest Result<T, E> from functional programming into Typescript.
 * inspired by:
 * - Rust
 * - Go Error handler
 * - https://github.com/badrap/result
 * - https://morioh.com/p/d23044ebd43f
 * - https://imhoff.blog/posts/using-results-in-typescript
 * - https://github.com/tc39/proposal-pattern-matching
 * - https://github.com/OliverBrotchie/optionals
 */

import { BaseError } from "../errors/base_error"
import { modelFactory } from "./model_factory"
import type { AnyValue, DeepPartial } from "./types/custom_type.types"

export class ErrorResult
  extends BaseError {
  constructor(
    public name = `ERROR_RESULT`,
    public message = `result error`,
    public debug?: AnyValue,
    public cause?: any, // Because different TS version always change the cause type. use `any` type instead
  ) {
    super(message)
    // Super(message)
  }

  static factory = modelFactory(ErrorResult, { allAutoGenerated: true },
  )
}
export class MyError extends Error {}

export abstract class ResultBase {
  public abstract unwrap()
}

export class ResultOk<T> extends ResultBase {
  public readonly isOk = true
  public readonly error = void 0
  public readonly errType = void 0

  constructor(
    public readonly value: T,
  ) {
    super()
  }

  public unwrap() {
    return this.value
  }
}

export class ResultError<E extends ErrorResult | undefined = ErrorResult, _Type extends string = `UNKNOWN`> extends ResultBase {
  public readonly isOk = false
  public readonly value = void 0

  constructor(
    public readonly error: E | undefined,
    public readonly errType: _Type | `UNKNOWN`,
  ) {
    super()
  }

  public unwrap(): never {
    throw this.error
  }
}

export type Result<T, E extends ErrorResult | undefined = ErrorResult, _Type extends string = `UNKNOWN`> =
  | ResultOk<T>
  | ResultError<E, _Type>

/**
 * How to use? just call Result_.ok() when return OK, otherwise return error
 */
export const Result_ = {
  ok: <T>(value: T): Result<T, never> => new ResultOk(value),
  err: <I extends DeepPartial<ErrorResult>, E extends ErrorResult = ErrorResult, _Type extends string = `UNKNOWN`>(extendedError?: I | Error | string, tp?: _Type): Result<never, E, _Type> =>
    new ResultError<E, _Type>(
      typeof extendedError === `string`
        ? ErrorResult.factory({ message: extendedError, name: tp }) as E
        : ErrorResult.factory({ ...extendedError, name: tp }) as E,
      tp ?? `UNKNOWN` as _Type,
    ),
}

export type Guarded<Y, N = AnyValue> = { matches: true; value: Y } | { matches: false; value: N }
export const typeGuarded = <Y, N = AnyValue>(v: Y | N, matches: boolean): Guarded<Y, N> => matches ? { matches: true, value: v as Y } : { matches: false, value: v as N }
export const typeValidateResult = <Y, I extends ErrorResult = ErrorResult>(v: AnyValue, matches: boolean, error?: string | I): Result<Y> => matches ? Result_.ok(v as Y) : Result_.err(error)

export type GetArgumentType<T> = T extends (...arguments_: infer U) => any ? U : never
